import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@api/api.service';
import { DocumentCreateSource } from '@api/types';
import { RavIcon } from '@shared/icon/icon';
import { EchoService } from '@shared/services/echo.service';
import { FolderReferenceService } from '@shared/services/folder-reference.service';
import { ImportStatusService } from '@shared/services/import-status.service';
import { InboxCountService } from '@shared/services/inbox-count.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { PreferencesService } from '@shared/services/preferences.service';
import { SidebarService } from '@shared/services/sidebar.service';
import { BatchImportJob, ImportJob, ImportType, TreeNode } from '@shared/types/import';
import { UserSettingsKeys } from '@shared/types/settings-keys';
import { getMimeTypeFromExtension } from '@shared/utils/file-type';

import { get } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
  selector: 'rav-import-manager',
  templateUrl: './import-manager.component.html',
  styleUrls: ['./import-manager.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(200%)' }),
        animate('500ms ease-in', style({ transform: 'translateX(0%)' }))
      ])
    ])
  ]
})
export class ImportManagerComponent implements OnInit, OnDestroy {
  RavIcon = RavIcon;
  status: any = {
    importingJobs: 0,
    completeJobs: 0,
    jobs: []
  };

  open = false;
  summary: string;

  shouldShowStatus = true;

  readonly MAX_REQUESTS: number = 20;
  // Set certain delay between each upload request.
  readonly REQUEST_DELAY: number = 5;
  subscriptions: Subscription[] = [];

  processingQueue: BatchImportJob[] = [];
  queueChecking = false;

  documentNotification = true;
  tableAutoRefreshOnImport = true;

  documentDownloadingWith202 = {};

  processedDocuments = {}; // Documents finished processing.

  accountId: string;

  constructor(
    private importStatusService: ImportStatusService,
    private api: ApiService,
    private sidebar: SidebarService,
    private inboxCount: InboxCountService,
    private folderReference: FolderReferenceService,
    private router: Router,
    private echo: EchoService,
    private localStorage: LocalStorageService,
    private folderReferenceService: FolderReferenceService,
    private preferences: PreferencesService
  ) {
    this.accountId = get(localStorage, 'userWithToken.account.id', '');
  }

  async ngOnInit(): Promise<void> {
    this.processingQueue = []; // This is a blocking queue.
    this.documentNotification = await this.preferences.getSetting('User', UserSettingsKeys.documentNotifications);
    this.tableAutoRefreshOnImport = await this.preferences.getSetting(
      'User',
      UserSettingsKeys.tableAutoRefreshOnImport
    );
    this.subscriptions.push(
      this.preferences.documentNotificationChangedAnnounce$.subscribe((setting) => {
        this.documentNotification = setting;
      }),

      this.preferences.tableAutoRefreshOnImportChangedAnnounce$.subscribe((setting) => {
        this.tableAutoRefreshOnImport = setting;
      }),

      this.importStatusService.documentNotToShowAsImportAnnouncedSource$.subscribe((documentID) => {
        this.documentDownloadingWith202[documentID] = true;
      }),

      this.importStatusService.createNewJobsAnnounced$.subscribe((batchJob) => {
        if (batchJob && batchJob.type && batchJob.jobs && batchJob.jobs.length > 0) {
          for (let index = 0; index < batchJob.jobs.length; index++) {
            const job = batchJob.jobs[index];
            if (job && !job.isFinished && !job.isFailed) job.isImporting = true;
            if (batchJob.type === ImportType.File && !this.documentNotification) continue;
            this.status.jobs.unshift(job);
          }
          if (this.status.jobs.length > 0) this.open = true;

          this.processingQueue.unshift(batchJob);
          this.updateSummary();
          // For performance reasons we update the inbox count until the batch is finished
          if (!this.queueChecking) this.checkQueue();
        }
      })
    );

    this.processedDocuments = {}; // For checking processing documents
    this.listenForNewDocumentSocket();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.resetStatus();
    this.processingQueue = [];
    this.processedDocuments = {};
    if (this.localStorage.userWithToken && this.localStorage.userWithToken.id) {
      this.echo.leaveChannel(`users.${this.localStorage.userWithToken.id}`);
    }
  }

  /**
   * Poll a new import job from the blocking queue.
   */
  async checkQueue(): Promise<void> {
    this.queueChecking = true;

    if (!this.processingQueue || this.processingQueue.length === 0) {
      this.queueChecking = false;
      return;
    }

    const currentJobs = this.processingQueue.pop();

    if (!currentJobs) {
      this.queueChecking = false;
      this.processingQueue = [];
      return;
    }

    try {
      await this.processJob(currentJobs);
    } catch (error) {
      throw error;
    } finally {
      this.checkQueue();
    }
  }

  /**
   * Processing the import job.
   */
  async processJob(batchJob: BatchImportJob): Promise<any> {
    switch (batchJob.type) {
      case ImportType.File:
        await this.importFiles(batchJob.jobs);
        break;
      case ImportType.Folder:
        await this.importFolder(batchJob.jobs);
        break;
      default:
        return;
    }
  }

  /**
   * Close current import manager window
   */
  close() {
    this.open = false;
    this.resetStatus();
  }

  /**
   * Open socket for new document imported
   */
  private listenForNewDocumentSocket(): void {
    // If document scanned by scanner then listen for socket to refresh the table view.
    this.echo
      .getPrivateChannel(`users.${this.localStorage.userWithToken.id}`)
      .listen('DocumentExportCreated', (event) => {
        const currentURL = this.router.url.split('?')[0];
        if (
          event.document &&
          event.document.source !== 'import' &&
          event.document.parent_id && // must have parent_id so not triggered for local USB/SMB scans
          !event.document.deleted_at
        ) {
          // if the socket event happened after the user entering the document,
          // which should not happen on for new scanned document but it happens on local/staging server.
          if (currentURL === `/documents/edit/${event.document.id}`) return;
          if (this.documentDownloadingWith202[event.document.id]) return;

          this.triggerDocumentNotification(event);
        } else if (event.document && event.document.id && event.document.source === 'import') {
          // For checking processing documents, if the user imports a jpeg, it will emit DocumentExportCreated
          this.processedDocuments[event.document.id] = true;
        }
      })
      .listen('DocumentProcessed', (event) => {
        // For checking processing documents, if the user imports a PDF file then it emit DocumentProcessed.
        if (event.document && event.document.id && event.document.source === 'import') {
          this.processedDocuments[event.document.id] = true;
        } else if (event.document && event.document.id && event.document.source === 'email') {
          // email imports trigger DocumentProcessed events
          this.triggerDocumentNotification(event);
        }
      });
  }

  /**
   * Trigger the importing folder event, notify sidebar and table to update if needed. Also update the status for importing process.
   *
   * @param jobs the import jobs
   */
  private async importFolder(jobs: ImportJob[]): Promise<void> {
    for (let index = 0; index < jobs.length; index++) {
      const job = jobs[index];

      if (!job || job.isFailed || job.isFinished) continue;

      try {
        await this.generateFolders(job);
        await this.generateFiles(job);
      } catch (error) {
        job.isFailed = true;
        throw error;
      } finally {
        job.isFinished = true;
        job.isImporting = false;

        this.updateSummary();

        this.sidebar.createdFolder.emit(job.rootFolder);

        const currentFolderId = await this.folderReference.getCurrentFolderId();
        if (job.rootFolder.parent_id === currentFolderId && this.tableAutoRefreshOnImport) {
          this.importStatusService.refreshCurrentTablePage();
        }
      }
    }
  }

  /**
   * Handle import file jobs.
   *
   * @param jobs Import Jobs
   */
  private async importFiles(jobs: ImportJob[]): Promise<void> {
    if (!jobs || jobs.length === 0) return;

    if (jobs.length === 1 && jobs[0].isFinished) return;

    let currentRequests = [];
    const folderID = jobs[0].selectedFolderId;

    try {
      for (let index = 0; index < jobs.length; index++) {
        const job = jobs[index];

        if (!job || job.isFailed || job.isFinished) continue;

        if (currentRequests.length === this.MAX_REQUESTS) {
          await Promise.all(currentRequests);
          currentRequests = [];
        }
        currentRequests.push(this.createFile(job.file.file, job.selectedFolderId, job, ImportType.File));
        await this.sleep(this.REQUEST_DELAY);
      }

      if (currentRequests.length > 0) {
        await Promise.all(currentRequests);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.folderReference.navigateToFolder(folderID);
    }
  }

  /**
   * Generate files from selected files.
   *
   * @param job current job
   */
  private async generateFiles(job: ImportJob): Promise<any> {
    try {
      let promises = [];

      while (job.fileImportingQueue.length > 0) {
        const treeNode = job.fileImportingQueue.pop();
        if (treeNode.isValid) {
          if (promises.length === this.MAX_REQUESTS) {
            await Promise.all(promises);
            promises = [];
          }
          promises.push(this.createFile(treeNode.file, treeNode.parent.id, job));
          await this.sleep(this.REQUEST_DELAY);
        }
      }
      if (promises.length > 0) {
        await Promise.all(promises);
        promises = [];
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generate all sub folder by BFS
   *
   * @param job Current importing job
   */
  private async generateFolders(job: ImportJob): Promise<any> {
    try {
      const queue = [];
      let folderNumber = 0;

      for (let i = 0; i < job.root.children.length; i++) {
        queue.unshift(job.root.children[i]);
      }

      while (queue.length > 0) {
        const size = queue.length;
        let createFolderPromises = [];

        for (let currentNodeIndex = 0; currentNodeIndex < size; currentNodeIndex++) {
          const currentNode = queue.pop();

          for (let childIndex = 0; childIndex < currentNode.children.length; childIndex++) {
            queue.unshift(currentNode.children[childIndex]);
          }

          if (currentNode.children.length > 0) {
            if (createFolderPromises.length === this.MAX_REQUESTS) {
              await Promise.all(createFolderPromises);

              createFolderPromises = [];
            }

            createFolderPromises.push(this.createFolder(currentNode, job));
            folderNumber = folderNumber + 1;

            await this.sleep(this.REQUEST_DELAY);
          } else {
            job.fileImportingQueue.unshift(currentNode);
          }
        }

        if (createFolderPromises.length > 0) {
          await Promise.all(createFolderPromises);

          createFolderPromises = [];
        }
      }

      job.redirectUrl = folderNumber > 0 ? `/folders/${job.root.children[0].id}` : '/inbox';
      return folderNumber;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a sub folder
   *
   * @param treeNode Current tree node
   * @param job Current importing job
   */
  private async createFolder(treeNode: TreeNode, job: ImportJob): Promise<void> {
    try {
      if (treeNode.parent.id) {
        const folder = await this.api.folders
          .post({
            title: treeNode.title,
            parent_id: treeNode.parent.id
          })
          .toPromise();

        treeNode.id = folder.id;

        if (treeNode.parent.id === job.root.id) {
          job.rootFolder = folder;
        }
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a single file into specified folder
   *
   * @param file File
   * @param folderID The folder's ID
   * @param job Current importing job
   * @param type Type of the import
   * @todo This function should be moved to a service
   */
  private async createFile(
    file: File,
    folderID: string,
    job: ImportJob,
    type: ImportType = ImportType.Folder
  ): Promise<void> {
    try {
      const fileName = file.name.substring(0, file.name.lastIndexOf('.'));
      const mimeType = getMimeTypeFromExtension(file.name.slice(file.name.lastIndexOf('.')));
      const formData = new FormData();

      formData.append('source', DocumentCreateSource.Import);
      formData.append('folder_id', folderID);
      formData.append('mime_type', mimeType);
      const requestResponse = await this.api.uploads.post(formData, fileName, file).toPromise();
      await this.waitForProcessingStatus(requestResponse.document.id);

      if (type === ImportType.File) job.redirectUrl = `/documents/edit/${requestResponse.document.id}`;
    } catch (error) {
      if (type === ImportType.File) job.isFailed = true;

      // Since we register that the file upload failed insted of throwing the error we just log it
      console.error('Error uploading file: %s Message: %s ', file.name, error.message);
    } finally {
      switch (type) {
        case ImportType.Folder:
          job.fileImported = job.fileImported + 1;
          break;
        case ImportType.File:
          job.isImporting = false;
          job.isFinished = true;
          const currentURL = this.router.url.split('?')[0];

          if (currentURL.includes('inbox') || currentURL.includes('folders')) {
            const currentFolderId = await this.folderReference.getCurrentFolderId();

            if (folderID === currentFolderId && this.tableAutoRefreshOnImport) {
              this.importStatusService.refreshCurrentTablePage();
            }
          }
          break;
      }
      this.updateSummary();
    }
  }

  /**
   * Wait for document to be fully processed from the socket event.
   *
   * @param documentID The imported document id
   */
  private waitForProcessingStatus(documentID: string): Promise<void> {
    return new Promise((resolve) => {
      const timer = setInterval(() => {
        if (this.processedDocuments[documentID]) {
          resolve();
          clearInterval(timer);
        }
      }, 200);
    });
  }

  /**
   * reset import status
   */
  private resetStatus(): void {
    this.status = {
      importingJobs: 0,
      completeJobs: 0,
      jobs: []
    };
  }

  /**
   * Keep the importing notification title updating when importing files or folders
   *
   * @param summary The title needs to show to the user.
   */
  private updateSummary(summary?: string): void {
    this.updateStatus();

    if (!summary) {
      if (this.status.importingJobs === 0) {
        this.summary = `${this.status.completeJobs} ${this.status.completeJobs > 1 ? 'imports' : 'import'} completed`;
      } else {
        this.summary = `Importing ${this.status.importingJobs} ${this.status.importingJobs > 1 ? 'items' : 'item'}`;
      }
    } else {
      this.summary = summary;
    }
  }

  /**
   * Checking current importing or completed jobs then update the status object
   */
  private updateStatus(): void {
    this.status.importingJobs = 0;
    this.status.completeJobs = 0;

    for (let index = 0; index < this.status.jobs.length; index++) {
      const job = this.status.jobs[index];

      if (job.isImporting) this.status.importingJobs++;
      if (job.isFinished) this.status.completeJobs++;
    }
  }

  /**
   * force function to sleep
   *
   * @param ms million seconds
   */
  private sleep(ms): Promise<any> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Triggers notification and refreshes table view for imports from scanner, desktop, mobile
   * or email imports
   *
   * @param event the DocumentExportCreated or DocumentProcessed socket event
   */
  private async triggerDocumentNotification(event: any) {
    const currentJob = new ImportJob(null, null, 1, event.document.title, null, event.document.folder_id);
    currentJob.isFinished = true;
    currentJob.redirectUrl = `/documents/edit/${event.document.id}`;

    this.importStatusService.createNewJobs([currentJob], ImportType.File);

    const currentFolderId = await this.folderReferenceService.getCurrentFolderId();
    if (currentFolderId === event.document.folder_id && this.tableAutoRefreshOnImport) {
      this.importStatusService.refreshCurrentTablePage();
    } else {
      try {
        // Get inbox, check if it is destination folder and update the count
        const inbox = await this.folderReferenceService.getInbox();
        if (event.document.folder_id === inbox.id) this.inboxCount.addOneToCurrentInboxCount();
      } catch (error) {} // Do nothing, inbox count will be updating when opening Inbox folder
    }
  }
}
